import { Link } from "react-router-dom";
import { FileText, UploadCloud } from "lucide-react";
import PageMeta from "./PageMeta";

const VIDEO_UPLOAD_FORM_URL = "https://docs.google.com/forms/d/e/1FAIpQLSccgB76zCvTibQeDiDbdQLmfNjK0aAb4ODY_vgTVyvXaHX8MQ/viewform";

// These always point to the LATEST entry from each list.
// Update these when a new document is published.
const current = {
  agenda: {
    label: "November 2026 Meeting Agenda",
    date: "2 November 2026",
    file: "/assets/Agenda_for_RSAA_Nov_2026_Meeting.pdf",
    color: "amber",
  },
  minutes: {
    label: "October 2026 Meeting",
    date: "5 October 2026",
    file: "/assets/Minutes_October_2026_meeting.pdf",
    color: "cyan",
  },
  treasurer: {
    label: "Treasurer's Report – November 2026",
    date: "28 October 2026",
    file: "/assets/Treasurers_Report_28_10_2026.pdf",
    color: "emerald",
  },
};

const colorMap = {
  amber:   { border: "border-amber-300/30",   bg: "bg-amber-400/5",   icon: "bg-amber-400/10 text-amber-300",   badge: "border-amber-300/20 bg-amber-400/10 text-amber-300",   label: "text-amber-300" },
  cyan:    { border: "border-cyan-300/30",    bg: "bg-cyan-400/5",    icon: "bg-cyan-400/10 text-cyan-300",    badge: "border-cyan-300/20 bg-cyan-400/10 text-cyan-300",    label: "text-cyan-300" },
  emerald: { border: "border-emerald-300/30", bg: "bg-emerald-400/5", icon: "bg-emerald-400/10 text-emerald-300", badge: "border-emerald-300/20 bg-emerald-400/10 text-emerald-300", label: "text-emerald-300" },
};

function DocRow({ item }) {
  const c = colorMap[item.color];
  return (
    <a
      href={item.file}
      target="_blank"
      rel="noopener noreferrer"
      className={`flex items-center gap-5 rounded-2xl border border-white/10 bg-white/5 p-5 transition hover:${c.border} hover:${c.bg}`}
    >
      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${c.icon}`}>
        <FileText className="h-6 w-6" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-white">{item.label}</p>
        <p className="mt-0.5 text-xs text-slate-400">{item.date}</p>
      </div>
      <span className={`shrink-0 rounded-lg border px-3 py-1 text-xs font-medium ${c.badge}`}>
        Open PDF
      </span>
    </a>
  );
}

export default function MembersPage() {
  return (
    <div className="page-shell min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <PageMeta title="Members' Documents | Redruth Sea Angling Association" noindex />
      <div className="mx-auto max-w-4xl px-6 py-16 sm:px-8 lg:px-12">

        <Link
          to="/"
          className="standalone-only inline-flex items-center gap-2 rounded-lg border border-cyan-300/40 bg-cyan-400/10 px-4 py-2 text-sm text-cyan-200 transition hover:bg-cyan-400/20"
        >
          ← Back
        </Link>

        <div className="mt-8">
          <p className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-300">Redruth Sea Angling Association</p>
          <h1 className="heading-glow mt-3 text-4xl font-bold tracking-tight text-white sm:text-5xl">Members' Documents</h1>
          <p className="mt-4 text-base leading-7 text-slate-400">
            Current agenda, minutes, and treasurer's report. Click any document to open the PDF.
          </p>
        </div>

        <div className="mt-10 space-y-6">

          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.25em] text-amber-300">Current Agenda</p>
            <DocRow item={current.agenda} />
            <Link to="/agenda" className="mt-2 inline-block text-xs text-slate-500 hover:text-slate-300 transition">View archived agendas →</Link>
          </div>

          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.25em] text-cyan-300">Latest Minutes</p>
            <DocRow item={current.minutes} />
            <Link to="/minutes" className="mt-2 inline-block text-xs text-slate-500 hover:text-slate-300 transition">View archived minutes →</Link>
          </div>

          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.25em] text-emerald-300">Treasurer's Report</p>
            <DocRow item={current.treasurer} />
            <Link to="/treasurer" className="mt-2 inline-block text-xs text-slate-500 hover:text-slate-300 transition">View archived reports →</Link>
          </div>

          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.25em] text-purple-300">Share Your Videos & Large Files</p>
            <a
              href={VIDEO_UPLOAD_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-5 rounded-2xl border border-white/10 bg-white/5 p-5 transition hover:border-purple-300/30 hover:bg-purple-400/5"
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-purple-400/10 text-purple-300">
                <UploadCloud className="h-6 w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-white">Club Video & File Upload <span className="font-normal text-slate-400">— you will need a Google account to allow upload</span></p>
                <p className="mt-0.5 text-xs text-slate-400">Upload your fish catches and fishing adventure videos for us to add to the RSAA YouTube channel. The upload facility has a high file-size limit, so you can send longer, high-quality videos without having to reduce their quality first. Only Graeme will have access to the original videos, so please don't worry about revealing your secret fishing marks—he will ensure that any recognisable locations are disguised before the videos are published.</p>
              </div>
              <span className="shrink-0 rounded-lg border border-purple-300/20 bg-purple-400/10 px-3 py-1 text-xs font-medium text-purple-300">
                Open Form
              </span>
            </a>
          </div>

        </div>

      </div>
    </div>
  );
}
